import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Database, Trash2, Pencil } from "lucide-react";
import api from "@/lib/api";
import { useAuth, apiErr } from "@/context/AuthContext";
import { rp } from "@/lib/format";
import { PageTitle, Panel, Pill } from "@/components/common";
import { Link } from "react-router-dom";
import StagingPanel from "@/components/StagingPanel";
import TelegramPanel from "@/components/TelegramPanel";
import BackupPanel from "@/components/BackupPanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export default function Admin() {
  const { isOwner, canManageUsers } = useAuth();
  const [tab, setTab] = useState("products");

  return (
    <div>
      <PageTitle title="Master Data & Admin" desc="Kelola produk, barang bon, staging, konfigurasi & data uji." />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4 flex-wrap h-auto">
          <TabsTrigger value="products" data-testid="admin-tab-products">Produk</TabsTrigger>
          <TabsTrigger value="bonitems" data-testid="admin-tab-bonitems">Barang Bon</TabsTrigger>
          <TabsTrigger value="staging" data-testid="admin-tab-staging">Staging (Pending)</TabsTrigger>
          <TabsTrigger value="telegram" data-testid="admin-tab-telegram">Telegram & AI</TabsTrigger>
          <TabsTrigger value="backup" data-testid="admin-tab-backup">Backup</TabsTrigger>
          {isOwner && <TabsTrigger value="settings" data-testid="admin-tab-settings">Konfigurasi</TabsTrigger>}
          {canManageUsers && <TabsTrigger value="users" data-testid="admin-tab-users">Pengguna</TabsTrigger>}
          {isOwner && <TabsTrigger value="audit" data-testid="admin-tab-audit">Audit Log</TabsTrigger>}
          <TabsTrigger value="data" data-testid="admin-tab-data">Data Uji</TabsTrigger>
        </TabsList>

        <TabsContent value="products"><Products /></TabsContent>
        <TabsContent value="bonitems"><BonItems /></TabsContent>
        <TabsContent value="staging"><StagingPanel /></TabsContent>
        <TabsContent value="telegram"><TelegramPanel isOwner={isOwner} /></TabsContent>
        <TabsContent value="backup"><BackupPanel isOwner={isOwner} /></TabsContent>
        {isOwner && <TabsContent value="settings"><SettingsPanel /></TabsContent>}
        {canManageUsers && <TabsContent value="users"><Panel title="Pengguna"><p className="text-sm text-slate-600">Kelola pengguna kini berada di <Link to="/akun" className="underline font-semibold" data-testid="admin-users-link">AKUN SAYA → Kelola Pengguna</Link>.</p></Panel></TabsContent>}
        {isOwner && <TabsContent value="audit"><AuditPanel /></TabsContent>}
        <TabsContent value="data"><TestData isOwner={isOwner} /></TabsContent>
      </Tabs>
    </div>
  );
}

function Products() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ product_code: "", product_name: "", default_sewing_rate: "" });
  const load = () => api.get("/products").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const save = async () => {
    try {
      await api.post("/products", { ...f, default_sewing_rate: parseInt(f.default_sewing_rate || 0) });
      toast.success("Produk ditambahkan"); setOpen(false); setF({ product_code: "", product_name: "", default_sewing_rate: "" }); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Panel title="Master Produk" action={<Button size="sm" onClick={() => setOpen(true)} className="bg-slate-900 text-white" data-testid="btn-add-product"><Plus className="w-4 h-4 mr-1" />Tambah</Button>}>
      <table className="w-full text-left text-sm">
        <thead><tr className="border-b border-slate-200 text-xs uppercase tracking-wider text-slate-600">{["Kode", "Nama", "Tarif Default", "Status"].map((h) => <th key={h} className="px-3 py-2">{h}</th>)}</tr></thead>
        <tbody>{rows.map((p) => (
          <tr key={p.product_id} className="border-b border-slate-100"><td className="px-3 py-2.5 font-bold">{p.product_code}</td><td className="px-3 py-2.5">{p.product_name}</td><td className="px-3 py-2.5 mono">{rp(p.default_sewing_rate)}</td><td className="px-3 py-2.5"><Pill value={p.status} /></td></tr>
        ))}{rows.length === 0 && <tr><td colSpan={4} className="px-3 py-8 text-center text-slate-400">Belum ada produk.</td></tr>}</tbody>
      </table>
      <Dialog open={open} onOpenChange={setOpen}><DialogContent><DialogHeader><DialogTitle>Tambah Produk</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Kode Produk</Label><Input value={f.product_code} onChange={(e) => setF({ ...f, product_code: e.target.value })} placeholder="YURI" data-testid="product-code" /></div>
          <div><Label>Nama Produk</Label><Input value={f.product_name} onChange={(e) => setF({ ...f, product_name: e.target.value })} data-testid="product-name" /></div>
          <div><Label>Tarif Ongkos Default (Rp/PCS)</Label><Input type="number" value={f.default_sewing_rate} onChange={(e) => setF({ ...f, default_sewing_rate: e.target.value })} data-testid="product-rate" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Batal</Button><Button onClick={save} className="bg-slate-900 text-white" data-testid="product-submit">Simpan</Button></DialogFooter>
      </DialogContent></Dialog>
    </Panel>
  );
}

function BonItems() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ item_name: "", unit: "PCS", unit_price: "" });
  const [editId, setEditId] = useState(null);
  const load = () => api.get("/bon-items").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const save = async () => {
    const body = { ...f, unit_price: f.unit_price === "" ? null : parseInt(f.unit_price) };
    try {
      if (editId) { await api.put(`/bon-items/${editId}`, body); toast.success("Barang diperbarui"); }
      else { await api.post("/bon-items", body); toast.success("Barang ditambahkan"); }
      setOpen(false); setEditId(null); setF({ item_name: "", unit: "PCS", unit_price: "" }); load();
    }
    catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (b) => {
    if (!window.confirm(`Hapus barang "${b.item_name}"? Transaksi bon yang sudah tercatat tidak berubah.`)) return;
    try { await api.delete(`/bon-items/${b.id}`); toast.success("Barang dihapus"); load(); } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Panel title="Master Barang Pembantu (Bon)" action={<Button size="sm" onClick={() => setOpen(true)} className="bg-slate-900 text-white" data-testid="btn-add-bonitem"><Plus className="w-4 h-4 mr-1" />Tambah</Button>}>
      <table className="w-full text-left text-sm">
        <thead><tr className="border-b border-slate-200 text-xs uppercase tracking-wider text-slate-600">{["Nama", "Unit", "Harga Satuan", "Status", ""].map((h) => <th key={h} className="px-3 py-2">{h}</th>)}</tr></thead>
        <tbody>{rows.map((b) => <tr key={b.id} className="border-b border-slate-100"><td className="px-3 py-2.5 font-semibold">{b.item_name}</td><td className="px-3 py-2.5">{b.unit}</td><td className="px-3 py-2.5 mono">{b.unit_price != null ? rp(b.unit_price) : <span className="text-amber-600">belum diisi</span>}</td><td className="px-3 py-2.5"><Pill value={b.status} /></td>
          <td className="px-3 py-2.5 whitespace-nowrap">
            <Button size="sm" variant="outline" className="h-8 mr-1.5" onClick={() => { setEditId(b.id); setF({ item_name: b.item_name, unit: b.unit, unit_price: b.unit_price ?? "" }); setOpen(true); }} data-testid={`bonitem-edit-${b.id}`}><Pencil className="w-3.5 h-3.5 mr-1" />Edit</Button>
            <Button size="sm" variant="outline" className="h-8 text-rose-600 border-rose-200" onClick={() => del(b)} data-testid={`bonitem-delete-${b.id}`}><Trash2 className="w-3.5 h-3.5 mr-1" />Hapus</Button>
          </td></tr>)}
        {rows.length === 0 && <tr><td colSpan={5} className="px-3 py-8 text-center text-slate-400">Belum ada barang. Harga diisi manual saat transaksi.</td></tr>}</tbody>
      </table>
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditId(null); }}><DialogContent><DialogHeader><DialogTitle>{editId ? "Edit Barang Bon" : "Tambah Barang Bon"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Harga Satuan (Rp) — diisi admin</Label><Input type="number" value={f.unit_price} onChange={(e) => setF({ ...f, unit_price: e.target.value })} placeholder="Kosong = harga diisi saat transaksi/totalan" data-testid="bonitem-price" /></div>
          <div><Label>Nama Barang</Label><Input value={f.item_name} onChange={(e) => setF({ ...f, item_name: e.target.value })} placeholder="BENANG" data-testid="bonitem-name" /></div>
          <div><Label>Unit</Label>
            <Select value={f.unit} onValueChange={(v) => setF({ ...f, unit: v })}><SelectTrigger data-testid="bonitem-unit"><SelectValue /></SelectTrigger>
              <SelectContent>{["PCS", "METER", "ROLL", "LUSIN", "KG"].map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent></Select>
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Batal</Button><Button onClick={save} className="bg-slate-900 text-white" data-testid="bonitem-submit">Simpan</Button></DialogFooter>
      </DialogContent></Dialog>
    </Panel>
  );
}

function SettingsPanel() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/settings").then((r) => setS(r.data)); }, []);
  const save = async () => {
    try {
      await api.put("/settings", {
        reject_deduction_per_pcs: parseInt(s.reject_deduction_per_pcs || 0),
        reject_policy: s.reject_policy, production_days_mode: s.production_days_mode,
        fixed_production_days: parseInt(s.fixed_production_days || 26),
        reject_tolerance_pct: parseFloat(s.reject_tolerance_pct || 3),
      });
      toast.success("Konfigurasi disimpan");
    } catch (e) { toast.error(apiErr(e)); }
  };
  if (!s) return null;
  return (
    <Panel title="Konfigurasi Kebijakan (Configurable)">
      <p className="text-sm text-slate-500 mb-4">Kebijakan reject & hari produksi tidak di-hardcode. Atur sesuai kebijakan perusahaan.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl">
        <div><Label>Kebijakan Reject</Label>
          <Select value={s.reject_policy} onValueChange={(v) => setS({ ...s, reject_policy: v })}><SelectTrigger data-testid="cfg-reject-policy"><SelectValue /></SelectTrigger>
            <SelectContent>{[["NOT_PAID", "Tidak Dibayar"], ["DEDUCTED", "Dipotong"], ["REWORK", "Rework"], ["ACCEPTED", "Diterima"]].map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select>
        </div>
        <div><Label>Potongan Reject / PCS (Rp)</Label><Input type="number" value={s.reject_deduction_per_pcs} onChange={(e) => setS({ ...s, reject_deduction_per_pcs: e.target.value })} data-testid="cfg-reject-deduction" /></div>
        <div><Label>Mode Hari Produksi</Label>
          <Select value={s.production_days_mode} onValueChange={(v) => setS({ ...s, production_days_mode: v })}><SelectTrigger data-testid="cfg-days-mode"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="CALENDAR">Kalender Berjalan</SelectItem><SelectItem value="FIXED">Tetap</SelectItem></SelectContent></Select>
        </div>
        <div><Label>Hari Produksi Tetap</Label><Input type="number" value={s.fixed_production_days} onChange={(e) => setS({ ...s, fixed_production_days: e.target.value })} /></div>
        <div><Label>Toleransi Reject (%)</Label><Input type="number" step="0.1" value={s.reject_tolerance_pct} onChange={(e) => setS({ ...s, reject_tolerance_pct: e.target.value })} /></div>
      </div>
      <Button onClick={save} className="mt-4 bg-slate-900 text-white" data-testid="cfg-save">Simpan Konfigurasi</Button>
    </Panel>
  );
}

function UsersPanel() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", email: "", password: "", role: "VIEWER" });
  const load = () => api.get("/auth/users").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const save = async () => { try { await api.post("/auth/users", f); toast.success("Pengguna dibuat"); setOpen(false); setF({ name: "", email: "", password: "", role: "VIEWER" }); load(); } catch (e) { toast.error(apiErr(e)); } };
  const del = async (id) => { try { await api.delete(`/auth/users/${id}`); toast.success("Dihapus"); load(); } catch (e) { toast.error(apiErr(e)); } };
  return (
    <Panel title="Pengguna & Peran" action={<Button size="sm" onClick={() => setOpen(true)} className="bg-slate-900 text-white" data-testid="btn-add-user"><Plus className="w-4 h-4 mr-1" />Tambah</Button>}>
      <table className="w-full text-left text-sm">
        <thead><tr className="border-b border-slate-200 text-xs uppercase tracking-wider text-slate-600">{["Nama", "Email", "Peran", ""].map((h) => <th key={h} className="px-3 py-2">{h}</th>)}</tr></thead>
        <tbody>{rows.map((u) => (
          <tr key={u.id} className="border-b border-slate-100"><td className="px-3 py-2.5 font-semibold">{u.name}</td><td className="px-3 py-2.5 text-slate-600">{u.email}</td><td className="px-3 py-2.5"><Pill value={u.role} /></td>
            <td className="px-3 py-2.5">{u.role !== "OWNER" && <button onClick={() => del(u.id)} className="text-rose-500 hover:text-rose-700"><Trash2 className="w-4 h-4" /></button>}</td></tr>
        ))}</tbody>
      </table>
      <Dialog open={open} onOpenChange={setOpen}><DialogContent><DialogHeader><DialogTitle>Tambah Pengguna</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Nama</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="user-name" /></div>
          <div><Label>Email</Label><Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="user-email" /></div>
          <div><Label>Kata Sandi</Label><Input type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="user-password" /></div>
          <div><Label>Peran</Label>
            <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}><SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="ADMIN">Admin</SelectItem><SelectItem value="VIEWER">Viewer</SelectItem><SelectItem value="OWNER">Owner</SelectItem></SelectContent></Select>
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Batal</Button><Button onClick={save} className="bg-slate-900 text-white" data-testid="user-submit">Simpan</Button></DialogFooter>
      </DialogContent></Dialog>
    </Panel>
  );
}

function AuditPanel() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/audit-logs?limit=200").then((r) => setRows(r.data)); }, []);
  return (
    <Panel title="Audit Log">
      <div className="space-y-1.5 max-h-[500px] overflow-y-auto text-sm">
        {rows.map((a) => (
          <div key={a.id} className="flex items-center justify-between border-b border-slate-100 py-1.5">
            <div><span className="font-semibold text-slate-800">{a.action}</span> <span className="text-slate-500">{a.entity} · {a.entity_id}</span>{a.reason && <span className="text-slate-400"> · {a.reason}</span>}</div>
            <div className="text-xs text-slate-400">{a.user_email}</div>
          </div>
        ))}
        {rows.length === 0 && <div className="text-slate-400 py-8 text-center">Belum ada log.</div>}
      </div>
    </Panel>
  );
}

function TestData({ isOwner }) {
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [keep, setKeep] = useState("P101,P102");
  const seed = async () => { setBusy(true); try { const r = await api.post("/admin/seed-test-data", {}); toast.success(r.data.message); } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); } };
  const clear = async () => { setBusy(true); try { const r = await api.post("/admin/clear-test-data", {}); toast.success(`Data uji dihapus (${r.data.deleted.transactions} transaksi)`); } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); } };
  const resetAll = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/reset-all", { confirm, keep_worker_ids: keep.split(",").map((s) => s.trim()).filter(Boolean), delete_masters: true, delete_audit_and_backups: true });
      toast.success(`Reset selesai — ${r.data.deleted.meranggi_transactions} transaksi dihapus, worker dipertahankan: ${r.data.kept_workers.join(", ") || "-"}`);
      setConfirm("");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <Panel title="Data Uji (Test Data)">
      <p className="text-sm text-slate-500 mb-4">Data uji ditandai <code className="bg-slate-100 px-1 rounded">is_test=true</code> dan tidak mencampuri data produksi. Hapus tanpa menyentuh data asli.</p>
      <div className="flex gap-3">
        <Button onClick={seed} disabled={busy} className="bg-slate-900 text-white" data-testid="btn-seed-test"><Database className="w-4 h-4 mr-1.5" />Buat Data Uji</Button>
        {isOwner && <Button onClick={clear} disabled={busy} variant="outline" className="text-rose-600 border-rose-200" data-testid="btn-clear-test"><Trash2 className="w-4 h-4 mr-1.5" />Hapus Data Uji</Button>}
      </div>
      {isOwner && <div className="mt-6 border border-rose-300 bg-rose-50 rounded-lg p-4" data-testid="reset-all-box">
        <div className="text-xs font-bold text-rose-800 mb-1">RESET SEMUA DATA — MULAI DARI NOL</div>
        <p className="text-xs text-rose-700 mb-3">Menghapus SEMUA transaksi, job, totalan & lock, inbox AI, pinjaman/RJP/RPR, alert, ringkasan AI, produk, barang bon, audit log, riwayat backup, dan mereset nomor urut. Worker pada daftar di bawah dipertahankan. Tidak bisa dibatalkan.</p>
        <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
          <div className="flex-1"><div className="text-[11px] text-slate-600 mb-1">Worker ID yang dipertahankan (pisah koma)</div><Input value={keep} onChange={(e) => setKeep(e.target.value)} className="mono uppercase" data-testid="reset-keep-workers" /></div>
          <div className="flex-1"><div className="text-[11px] text-slate-600 mb-1">Ketik RESET untuk konfirmasi</div><Input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="RESET" className="mono" data-testid="reset-confirm-input" /></div>
          <Button onClick={resetAll} disabled={busy || confirm.trim().toUpperCase() !== "RESET"} className="bg-rose-700 hover:bg-rose-800 text-white" data-testid="btn-reset-all"><Trash2 className="w-4 h-4 mr-1.5" />Reset Semua Data</Button>
        </div>
      </div>}
    </Panel>
  );
}
